import { Button, FormControl, FormErrorMessage, HStack, Input } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PromoCode } from 'types/promocode';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import type { CheckoutData, Product } from '../useCheckoutForm';

/**
 * Logique de validation / application d'un code promo (partagée entre
 * l'ancien récapitulatif et le Nouveau Panier v2). Écrit `PromoCode`,
 * `TotalAmount` et `DiscountedAmount` dans le formulaire ; en cas de refus,
 * pose une erreur react-hook-form sur `PromoCode`.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePromoCodeApply(product: Product) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<CheckoutData>();
  const [appliedCode, discount] = watch(['PromoCode', 'DiscountedAmount']);

  function applyDiscount(promo: PromoCode) {
    const amount = getValues('Amount');

    if (promo.Discount.Type === 'Percent') {
      const discountedAmount = amount * (promo.Discount.Amount / 100);
      const totalAmount = Math.max(0, amount - discountedAmount); // Ne jamais descendre en dessous de 0
      setValue('TotalAmount', totalAmount);
      setValue('DiscountedAmount', discountedAmount);
    } else if (promo.Discount.Type === 'FlatRate') {
      const discountedAmount = promo.Discount.Amount;
      const totalAmount = Math.max(0, amount - discountedAmount); // Ne jamais descendre en dessous de 0
      setValue('TotalAmount', totalAmount);
      setValue('DiscountedAmount', discountedAmount);
    }
  }

  async function apply(rawCode: string | undefined): Promise<boolean> {
    const code = rawCode?.trim();
    if (!code) return false;

    setIsLoading(true);
    return HTTP.post<PromoCode>('/sale/promocode', {
      code,
      productName: product.Name,
    })
      .send()
      .then((promoCode) => {
        if (!promoCode.AllowOnline) {
          setError('PromoCode', { message: t("Ce code promo n'est pas utilisable en ligne") });
          return false;
        }

        if (!promoCode.Active) {
          setError('PromoCode', { message: t("Ce code promo n'est plus actif") });
          return false;
        }

        // Vérifier l'applicabilité du code promo
        const isApplicable =
          promoCode.ApplicableItems === null ||
          promoCode.ApplicableItems.some((item) => {
            // Pour les ServiceCategory, comparer avec ProgramId du produit
            if (item.Type === 'ServiceCategory' && product.type === 'service') {
              const matches = item.Id === product.ProgramId;
              return matches;
            }
            // Fallback: vérifier si le nom du produit contient le nom de la catégorie
            const nameMatch = product.Name.toLowerCase().includes(item.Name.toLowerCase());

            return nameMatch;
          });

        if (!isApplicable) {
          setError('PromoCode', {
            message: t("Ce code promo n'est pas applicable à ce produit"),
          });
          return false;
        }

        setValue('PromoCode', promoCode.Code);
        applyDiscount(promoCode);
        return true;
      })
      .catch((e: unknown) => {
        // Un code déjà consommé par quelqu'un d'autre a son propre message :
        // « invalide » enverrait le client au service client pour rien.
        setError('PromoCode', {
          message: e instanceof ApiClientException ? e.getMessage() : t('Code promo invalide'),
        });
        return false;
      })
      .finally(() => setIsLoading(false));
  }

  function clear() {
    setValue('PromoCode', '');
    setValue('DiscountedAmount', 0);
    setValue('TotalAmount', getValues('Amount'));
    clearErrors('PromoCode');
  }

  return { apply, clear, clearErrors, isLoading, error: errors.PromoCode?.message, appliedCode, discount };
}

export function CheckoutPromoCode({ product }: { product: Product }) {
  const { apply, clearErrors, isLoading, error } = usePromoCodeApply(product);
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <FormControl isInvalid={!!error}>
      <HStack className="PromoCodeInput">
        <Input
          placeholder="Utiliser un code promo"
          ref={inputRef}
          onFocus={() => clearErrors('PromoCode')}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              apply(inputRef.current?.value);
            }
          }}
        />
        <Button isLoading={isLoading} onClick={() => apply(inputRef.current?.value)}>
          Appliquer
        </Button>
      </HStack>

      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
}
